import type { Space } from '@mail/cross-sizes-utils';
import { UserFullQuotaAPICall } from 'reactApp/api/user/FullQuotaAPICall';
import { overquotaModalConfig } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { overquotaDebugLogger } from 'reactApp/appHelpers/featuresHelpers/features/overquotaStates';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { productsController } from 'reactApp/modules/products/products.controller';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { EProductPeriod } from 'reactApp/modules/products/products.types';
import { sendOverquotaInfo } from 'reactApp/modules/profile/profile.helpers';
import { setProfileLoadStateSuccess } from 'reactApp/modules/profile/profile.module';
import { handleLoadUserFullProfile } from 'reactApp/modules/profile/profile.saga';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { trashbinUpdateSizeSuccess } from 'reactApp/modules/trashbin/trashbin.module';
import { getTrashbinSize, isTrashSizeLoaded, isTrashSizeLoading } from 'reactApp/modules/trashbin/trashbin.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { renderMobileSplashScreen } from 'reactApp/ui/Mobile/SplashScreen/helpers/SplashScreen.helpers';
import { useOverquotaSplashScreen } from 'reactApp/ui/Mobile/SplashScreen/hooks/useOverquotaSplashScreen';
import { renderOverquotaModal } from 'reactApp/ui/OverquotaModal/OverquotaModal.helpers';
import { renderOverquotaSplash } from 'reactApp/ui/OverquotaPopup/newSplash/OverquotaSplash.helpers';
import { ConditionsCheckType, getCheckAllConditions } from 'reactApp/utils/conditionChecker';
import { captureException } from 'reactApp/utils/tracer';
import { delay, put, race } from 'redux-saga/effects';
import { ON_PREMISE } from 'server/constants/environment';
import type { QuotaStates } from 'server/types/context/IUser';
import { call, select, take } from 'typed-redux-saga';

import { checkEnvironmentOverquotaConditions } from './checkEnvironmentOverquotaConditions.saga';

const WAIT_QUOTA_API_TIMEOUT = 5000;

const checkAllConditionsFalse = getCheckAllConditions(overquotaDebugLogger, ConditionsCheckType.FirstTrue);
const checkAllConditionsTrue = getCheckAllConditions(overquotaDebugLogger, ConditionsCheckType.FirstFalse);

function* setProfileOverquota(over: Space) {
    if (over.original <= 0) {
        return;
    }

    const isSizeLoading = yield* select(isTrashSizeLoading);
    let isSizeLoaded = yield* select(isTrashSizeLoaded);
    let trashSize = yield* select(getTrashbinSize);

    if (isSizeLoading && !isSizeLoaded) {
        yield take(trashbinUpdateSizeSuccess);
        isSizeLoaded = true;
        trashSize = yield* select(getTrashbinSize);
    }

    if (!isSizeLoaded) {
        const { data } = yield new UserFullQuotaAPICall().makeRequest({ trash: true });

        if (data.status === 200) {
            trashSize = data.body.trash_size || 0;
        }
    }

    if (over.original < trashSize) {
        yield put(setProfileLoadStateSuccess({ trashSize }));
    }
}

function* tryRenderOverquotaCommunication({
    isPhone,
    state,
    isOverquotaFromProfileApi,
}: {
    isPhone: boolean;
    state: QuotaStates | undefined;
    isOverquotaFromProfileApi: boolean;
}) {
    if (state === 'cleared' || state === 'after_clear') {
        yield* call(renderOverquotaSplash, {
            mode: state,
        });

        return;
    }

    const { over } = yield* select(UserSelectors.getCloudSpace);

    if (isPhone && state !== 'blocked') {
        yield productsController.loadProducts();
        const { product } = yield* select(ProductsSelectors.getProductAndTariffByQuota, over.original, EProductPeriod.year, false);

        yield* call(renderMobileSplashScreen, {
            contentHook: useOverquotaSplashScreen,
            hookParams: { productId: product?.id || '' },
        });

        return;
    }

    try {
        setProfileOverquota(over);
    } catch (error) {
        captureException(error, { issueKey: 'overquota/setProfileOverquota' });
    }

    if (state === 'blocked') {
        yield* call(renderOverquotaSplash, {
            mode: state,
        });

        return;
    }

    if (isOverquotaFromProfileApi && overquotaModalConfig.enabled) {
        yield* call(renderOverquotaModal);
    }
}

export function* initOverquotaProcessing() {
    const isAnonymous = yield* select(UserSelectors.isAnonymous);
    const isBizUser = yield* select(UserSelectors.isBizUser);
    const isCorp = yield* select(UserSelectors.isCorpUser);

    const overquotaEnable = checkAllConditionsFalse('initOverquotaProcessing', {
        isAnonymous,
        isBizUser,
        isCorp,
        ON_PREMISE,
    });

    if (!overquotaEnable) {
        return;
    }

    try {
        yield* handleLoadUserFullProfile(false, true, false);
        const quotaState = yield* select(getUserOverquotaState);
        const state = quotaState?.state;

        // Если нужно будет проверять квоту, а она ещё не загрузилась
        const isQuotaLoaded = yield* select(UserQuotaSelectors.isQuotaLoaded);
        if (!isQuotaLoaded) {
            const { timeout } = yield race({
                success: take([loadUserQuotaSuccess.toString()]),
                timeout: delay(WAIT_QUOTA_API_TIMEOUT),
            });
            if (timeout) {
                return;
            }
        }

        const isPhone = yield* select(EnvironmentSelectors.isPhone);
        const isOverQuotaCloud = yield* select(UserSelectors.isOverQuotaUser);
        const threshold = yield* select(UserSelectors.getUserThreshold);
        const overquotaType = yield* select(UserQuotaSelectors.getOverquotaType);

        overquotaDebugLogger.info('UserInfo', {
            threshold,
            state,
            dates: {
                startPipeline: quotaState?.startPipeline && new Date(quotaState?.startPipeline).toLocaleString(),
                clearTime: quotaState?.clearTime && new Date(quotaState?.clearTime).toLocaleString(),
                blockTime: quotaState?.blockTime && new Date(quotaState?.blockTime).toLocaleString(),
                now: new Date().toLocaleString(),
            },
            overquotaType,
        });

        const isOverQuotaMailAndCloud = yield* select(UserQuotaSelectors.isOverquota);
        const isOverquotaFromProfileApi = yield* select(UserQuotaSelectors.isOverquotaBySpaceAndApi);

        /**
         * Пользователь зажимается если квота превышена + правильный статус с бэкенда
         */
        const isOverquotaCheckOk = checkAllConditionsTrue('initOverquotaProcessing (quota check new)', {
            isOverquotaFromProfileApi: { check: isOverquotaFromProfileApi },
        });

        // Проверяем некоторые специфические условия, связанные с платформами и текущим storage
        const isEnvironmentConditionsOk = yield* call(checkEnvironmentOverquotaConditions, { state });

        if (quotaState?.status === 'failed') {
            captureException(null, {
                issueKey: 'overquota/failedStatus',
                message: 'failed state api call',
                state: String(state),
                quotaState: quotaState ? JSON.stringify(quotaState) : 'undefined',
                isOverQuotaCloud,
                isOverQuotaMailAndCloud,
                time: Date.now().toLocaleString(),
            });
        }

        sendOverquotaInfo({
            overquotaStatus: quotaState?.status === 'failed' ? 'failedapi' : state,
            overquotaResolve: isOverquotaCheckOk,
            stopProcessing: !isEnvironmentConditionsOk,
            threshold,
        });

        if (!isEnvironmentConditionsOk || !isOverquotaCheckOk) {
            return;
        }

        yield tryRenderOverquotaCommunication({ isPhone, state, isOverquotaFromProfileApi });
    } catch (error: any) {
        captureException(error, { issueKey: 'overquota/init' });
    }
}
